import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
// import { UG, KE } from "country-flag-icons/react/3x2";
import lottie from "lottie-web";
import BleepAnimationDark from "../components/lottie/BleepAnimationDark.json";
import { createAvatar } from "@dicebear/core";
import { identicon } from "@dicebear/collection";

const CATEGORIES = [
  "Agriculture",
  "Apparel",
  "Automobiles",
  "Banking",
  "Beverages",
  "Chemicals",
  "Construction",
  "Cosmetics",
  "Currencies",
  "Education",
  "Electronics",
  "Food",
  "Government",
  "Media",
  "Organizations",
  "Petroleum",
  "Pharmaceuticals",
  "Other",
];

const Navbar = ({ domain, userData, signOut, search }) => {
  const navigate = useNavigate();
  const userId = userData[0];
  const userType = userData[1];
  const showSearch = !userId || userType === "user";
  /* Same reading of the type cookie as App.js and useAccount: no type at all is
     a regular user. Staff have no feed on /me, so the account entry skips it. */
  const isStaff = !!userType && userType !== "user";
  const userName = userData[2];
  const userEmail = userData[3];
  const userPassword = userData[4];
  const userOrg = userData[5];
  const userPermissions = userData[6];
  const userAnonymous = userData[7];
  const [displayName, setDisplayName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  // update search query
  useEffect(() => {
    setSearchQuery(search);
  }, [search]);

  // Focus search input when '/' key is pressed
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === "/" &&
        document.activeElement !== searchInputRef.current
      ) {
        event.preventDefault(); // Prevent typing '/' in input
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // navigate, not window.location. Search is the most-used route in the app and
  // a document load here repainted from the browser's blank canvas every time.
  // /search derives its query straight from location.search, so arriving there
  // from /search updates in place rather than remounting.
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  useEffect(() => {
    if (userAnonymous === "true" && userId) {
      setDisplayName(`Bleeper#${userId.slice(-6)}`);
    } else {
      setDisplayName(userName);
    }
  }, [userId, userAnonymous, userName]);

  const avatar = React.useMemo(() => {
    return createAvatar(identicon, {
      seed: displayName,
      backgroundType: ["gradientLinear", "solid"],
    }).toDataUri();
  }, [displayName]);

  /* ---- NEW: preferred user profile picture (pfp) from API ---- */
  const [pfpUrl, setPfpUrl] = useState("");
  useEffect(() => {
    // Clear when signed out / id missing
    if (!userId) {
      setPfpUrl("");
      return;
    }

    const requestOptions = {
      method: "GET",
      redirect: "follow",
    };

    fetch(`/api/users/${userId}`, requestOptions)
      .then((response) => response.text())
      .then((result) => {
        try {
          const data = result ? JSON.parse(result) : {};
          if (data && typeof data.pfp === "string" && data.pfp.length > 0) {
            setPfpUrl(data.pfp);
          } else {
            setPfpUrl(""); // fallback to identicon
          }
        } catch {
          setPfpUrl(""); // fallback to identicon on parse error
        }
      })
      .catch((error) => {
        console.error(error);
        setPfpUrl(""); // fallback to identicon on fetch error
      });
  }, [userId]);
  /* ---- END NEW ---- */

  // choose actual image to render (API pfp first, then identicon)
  const avatarSrc = pfpUrl || avatar;

  const [isHovered, setIsHovered] = useState(false);
  const bleepanimation = React.useRef(null);
  useEffect(() => {
    if (bleepanimation.current && !bleepanimation.current.animationLoaded) {
      bleepanimation.current.animationLoaded = true; // Mark animation as loaded to prevent re-render
      lottie.loadAnimation({
        container: bleepanimation.current,
        renderer: "canvas",
        loop: true,
        autoplay: true,
        animationData: BleepAnimationDark,
      });
    }
  }, []);

  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const mobileSearchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const mobilePanelRef = useRef(null);
  const debounceRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    if (showMobileSearch && mobileSearchRef.current) {
      mobileSearchRef.current.focus();
    }
  }, [showMobileSearch]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setShowMobileSearch(false);
        setShowSuggestions(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveIndex(-1);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/posts/suggestions?q=${encodeURIComponent(q)}`,
        );
        const data = await res.json();
        setSuggestions(Array.isArray(data) ? data : []);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  useEffect(() => {
    const handler = (e) => {
      const inDesktop = suggestionsRef.current?.contains(e.target);
      const inMobile = mobilePanelRef.current?.contains(e.target);
      if (!inDesktop && !inMobile) {
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const catSuggestions =
    searchQuery.trim().length >= 2
      ? CATEGORIES.filter((c) =>
          c.toLowerCase().includes(searchQuery.toLowerCase()),
        ).slice(0, 3)
      : [];

  const allSuggestionItems = [
    ...catSuggestions.map((cat) => ({ type: "category", text: cat })),
    ...suggestions.map((keyword) => ({ type: "keyword", text: keyword })),
  ];

  const handleKeyDown = (e) => {
    if (!showSuggestions || allSuggestionItems.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        Math.min(prev + 1, allSuggestionItems.length - 1),
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      const item = allSuggestionItems[activeIndex];
      if (item.type === "category") {
        navigate(`/search?category=${encodeURIComponent(item.text)}`);
      } else {
        navigate(`/search?q=${encodeURIComponent(item.text)}`);
      }
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  };

  return (
    <>
      {/* bleep-light is load bearing: it scopes the --bl-* tokens the menus
        read, and it is what lets the theme neutralise App.css's solid-orange
        .dropdown-item:hover instead of an !important override. */}
      <header className="sticky-top bleep-light">
        <div className="bleep-navbar">
          <div
            className="d-flex align-items-center"
            style={{ paddingBlock: "8px" }}
          >
            <div className="row w-100">
              <div
                className="col-2 justify-content-start align-items-center"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  paddingLeft: "40px",
                }}
              >
                <Link
                  to={
                    userType == "user" || userType == null ? "/" : "/dashboard"
                  }
                  className="text-decoration-none d-flex align-items-center"
                >
                  <div
                    style={{ position: "relative", display: "inline-block" }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    {/* Image */}
                    <img
                      src="/images/bleeplogo_primary.png"
                      style={{
                        width: "140px",
                        transition: "opacity 0.5s ease",
                      }}
                    />
                  </div>
                </Link>
              </div>

              {showSearch && (
                <div
                  className="col-5 col-xl-5 d-none d-lg-flex justify-content-start align-items-center"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {/* Search Input */}
                  <form
                    ref={suggestionsRef}
                    onSubmit={(e) => {
                      handleSearch(e);
                      setShowSuggestions(false);
                    }}
                    className="d-flex align-items-center w-100"
                    style={{ position: "relative" }}
                  >
                    <button
                      type="submit"
                      className="btn"
                      style={{
                        position: "absolute",
                        left: "10px",
                        background: "none",
                        border: "none",
                      }}
                    >
                      <i className="fas fa-search"></i>
                    </button>
                    <input
                      type="text"
                      ref={searchInputRef}
                      className="form-control bleep-search-bar"
                      placeholder="Search 5000+ Bleeps"
                      value={
                        activeIndex >= 0 && allSuggestionItems[activeIndex]
                          ? allSuggestionItems[activeIndex].text
                          : searchQuery
                      }
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setActiveIndex(-1);
                        setShowSuggestions(e.target.value.trim().length >= 2);
                      }}
                      onKeyDown={handleKeyDown}
                      onFocus={() => {
                        if (searchQuery.trim().length >= 2)
                          setShowSuggestions(true);
                      }}
                      autoComplete="off"
                    />

                    <kbd
                      style={{
                        position: "absolute",
                        right: 16,
                        top: "50%",
                        transform: "translateY(-50%)",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        background: "#fff",
                        border: "1.5px solid rgba(0,0,0,0.13)",
                        boxShadow: "0 1px 0 rgba(0,0,0,0.12)",
                        fontFamily: "system-ui, sans-serif",
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#888",
                        pointerEvents: "none",
                        lineHeight: 1,
                      }}
                    >
                      /
                    </kbd>

                    {showSuggestions &&
                      allSuggestionItems.length > 0 &&
                      (() => {
                        let itemIdx = -1;
                        return (
                          <div className="bl-suggest">
                            {catSuggestions.length > 0 && (
                              <>
                                <div className="bl-menu__label">Categories</div>
                                {catSuggestions.map((cat) => {
                                  itemIdx++;
                                  const idx = itemIdx;
                                  const isActive = activeIndex === idx;
                                  return (
                                    <Link
                                      key={cat}
                                      to={`/search?category=${encodeURIComponent(cat)}`}
                                      className="bl-suggest__item"
                                      style={{
                                        background: isActive
                                          ? "rgba(255,81,0,0.07)"
                                          : undefined,
                                      }}
                                      onMouseEnter={() => setActiveIndex(idx)}
                                      onMouseLeave={() => setActiveIndex(-1)}
                                      onClick={() => {
                                        setShowSuggestions(false);
                                        setActiveIndex(-1);
                                      }}
                                    >
                                      <i className="fa-solid fa-tag bl-suggest__icon bl-suggest__icon--brand" />
                                      <span>{cat}</span>
                                    </Link>
                                  );
                                })}
                              </>
                            )}

                            {catSuggestions.length > 0 &&
                              suggestions.length > 0 && (
                                <hr className="bl-menu__divider" />
                              )}

                            {suggestions.length > 0 && (
                              <>
                                {suggestions.map((keyword) => {
                                  itemIdx++;
                                  const idx = itemIdx;
                                  const isActive = activeIndex === idx;
                                  return (
                                    <Link
                                      key={keyword}
                                      to={`/search?q=${encodeURIComponent(keyword)}`}
                                      className="bl-suggest__item"
                                      style={{
                                        background: isActive
                                          ? "rgba(255,81,0,0.07)"
                                          : undefined,
                                      }}
                                      onMouseEnter={() => setActiveIndex(idx)}
                                      onMouseLeave={() => setActiveIndex(-1)}
                                      onClick={() => {
                                        setShowSuggestions(false);
                                        setActiveIndex(-1);
                                      }}
                                    >
                                      <i className="fas fa-search bl-suggest__icon" />
                                      <span>{keyword}</span>
                                    </Link>
                                  );
                                })}
                              </>
                            )}

                            <div
                              style={{
                                borderTop: "1px solid rgba(0,0,0,0.06)",
                                padding: "6px 6px",
                              }}
                            >
                              <button
                                type="submit"
                                className="bl-suggest__item"
                              >
                                <i className="fas fa-search bl-suggest__icon bl-suggest__icon--brand" />
                                <span>
                                  Search for "<strong>{searchQuery}</strong>"
                                </span>
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                  </form>
                </div>
              )}

              <div className="col d-flex justify-content-end align-items-center">
                <div className="d-none d-md-flex">
                  {showSearch ? (
                    <div className="d-md-flex justify-content-end">
                      <Link to="/store" className="nav-pill">
                        <span className="nav-btn">
                          <i className="fa-solid fa-shop"></i> Buy
                        </span>
                      </Link>
                    </div>
                  ) : (
                    userId && (
                      <div className="d-md-flex justify-content-end align-items-center">
                        <div
                          className="bl-plan-badge"
                          title="Your account is on our Enterprise Plan"
                        >
                          <i className="fa-solid fa-gem" aria-hidden="true" />
                          Enterprise Plan
                        </div>
                      </div>
                    )
                  )}

                  {/* Need Help? dropdown with uniform icon boxes */}
                  <div className="d-md-flex justify-content-end">
                    <div className="dropdown">
                      <a
                        href="#"
                        className="nav-pill nav-btn dropdown-toggle"
                        id="helpDropdown"
                        role="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <i className="fa-solid fa-question-circle"></i>
                        Need Help?
                      </a>

                      <ul
                        className="dropdown-menu dropdown-menu-end bl-menu"
                        aria-labelledby="helpDropdown"
                      >
                        {/* Help & Support section */}
                        <li>
                          <div className="bl-menu__label">Help & Support</div>
                        </li>

                        <li>
                          <Link
                            className="dropdown-item bl-menu__item"
                            to="/contact"
                          >
                            <span className="bl-menu__icon">
                              <i className="fa-regular fa-envelope-open" />
                            </span>
                            <div>
                              <div className="bl-menu__title">Contact Us</div>
                              <div className="bl-menu__sub">
                                Talk to our team
                              </div>
                            </div>
                          </Link>
                        </li>

                        <li>
                          <Link
                            className="dropdown-item bl-menu__item"
                            to="/about"
                          >
                            <span className="bl-menu__icon">
                              <i className="fa-solid fa-info-circle" />
                            </span>
                            <div>
                              <div className="bl-menu__title">About</div>
                              <div className="bl-menu__sub">
                                Learn about BLEEP
                              </div>
                            </div>
                          </Link>
                        </li>

                        <li>
                          <Link
                            className="dropdown-item bl-menu__item"
                            to="/cbqs"
                          >
                            <span className="bl-menu__icon">
                              <i className="fa-solid fa-comments" />
                            </span>
                            <div>
                              <div className="bl-menu__title">CBQs</div>
                              <div className="bl-menu__sub">
                                Common BLEEP Questions
                              </div>
                            </div>
                          </Link>
                        </li>

                        {/* Divider */}
                        <li>
                          <hr className="bl-menu__divider" />
                        </li>

                        {/* Legal section */}
                        <li>
                          <div className="bl-menu__label">Legal</div>
                        </li>

                        <li>
                          <Link
                            className="dropdown-item bl-menu__item"
                            to="/termsofservice"
                          >
                            <span className="bl-menu__icon bl-menu__icon--muted">
                              <i className="fa-solid fa-file-contract" />
                            </span>
                            <div>
                              <div className="bl-menu__title">
                                Terms of Service
                              </div>
                              <div className="bl-menu__sub">Read our terms</div>
                            </div>
                          </Link>
                        </li>

                        <li>
                          <Link
                            className="dropdown-item bl-menu__item"
                            to="/privacypolicy"
                          >
                            <span className="bl-menu__icon bl-menu__icon--muted">
                              <i className="fa-solid fa-shield-halved" />
                            </span>
                            <div>
                              <div className="bl-menu__title">
                                Privacy Policy
                              </div>
                              <div className="bl-menu__sub">
                                Read our privacy policy
                              </div>
                            </div>
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {!userId ? (
                  <div className="d-flex align-items-center justify-content-end gap-2">
                    {/* Mobile search toggle button */}
                    {showSearch && (
                      <button
                        type="button"
                        onClick={() => setShowMobileSearch(true)}
                        className="nav-icon-btn text-decoration-none d-flex d-md-none align-items-center"
                        aria-controls="mobileSearchPanel"
                        aria-expanded={showMobileSearch}
                        aria-label="Open search"
                      >
                        <span className="nav-btn">
                          <i className="fas fa-search" />
                        </span>
                      </button>
                    )}

                    <Link
                      to="/signin"
                      className="text-decoration-none d-flex align-items-center"
                    >
                      <a className="account-btn">
                        <i className="fas fa-user-circle"></i>{" "}
                        <span className="d-none d-md-inline">Sign-in</span>
                      </a>
                    </Link>
                  </div>
                ) : (
                  <div className="d-flex align-items-center justify-content-end gap-2">
                    {/* Mobile search toggle button */}
                    {showSearch && (
                      <button
                        type="button"
                        onClick={() => setShowMobileSearch(true)}
                        className="nav-icon-btn text-decoration-none d-flex d-md-none align-items-center"
                        aria-controls="mobileSearchPanel"
                        aria-expanded={showMobileSearch}
                        aria-label="Open search"
                      >
                        <span className="nav-btn">
                          <i className="fas fa-search" />
                        </span>
                      </button>
                    )}

                    {/* A plain Bootstrap dropdown: a .dropdown wrapper, a real
                      <button> toggle, the menu as its sibling. It used to be a
                      50px div whose *inner* 40px div carried the toggle, so the
                      outer ring of the control did nothing when clicked. */}
                    <div className="dropdown">
                      <button
                        type="button"
                        className="bl-avatar-btn"
                        id="accountDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        aria-label="Your account"
                      >
                        <span className="bl-avatar-disc">
                          <img
                            src={avatarSrc}
                            alt=""
                            className={pfpUrl ? "is-photo" : undefined}
                          />
                        </span>
                      </button>
                      <ul
                        className="dropdown-menu dropdown-menu-end bl-menu"
                        aria-labelledby="accountDropdown"
                      >
                        <li className="bl-menu__head">
                          <span className="bl-avatar-btn">
                            <span className="bl-avatar-disc">
                              <img
                                src={avatarSrc}
                                alt=""
                                className={pfpUrl ? "is-photo" : undefined}
                              />
                            </span>
                          </span>
                          <div className="bl-menu__head-text">
                            <div className="bl-menu__name">{displayName}</div>
                            <div className="bl-menu__email">{userEmail}</div>
                          </div>
                        </li>

                        <li>
                          <div className="bl-menu__label">Account</div>
                        </li>

                        {/* One entry, landing on whichever half of the pair the
                            account has a use for: /me is the identity band over
                            the reporter's own feed, which is what they come back
                            for, while a staff account has no feed there and only
                            ever visits to change a detail. */}
                        <li>
                          <Link
                            className="dropdown-item bl-menu__item"
                            to={isStaff ? "/me/settings" : "/me"}
                          >
                            <span className="bl-menu__icon">
                              <i className="fa-solid fa-user-gear" />
                            </span>
                            <div>
                              <div className="bl-menu__title">My Account</div>
                              <div className="bl-menu__sub">
                                {isStaff
                                  ? "Profile and settings"
                                  : "Your bleeps, profile and settings"}
                              </div>
                            </div>
                          </Link>
                        </li>

                        <li>
                          <hr className="bl-menu__divider" />
                        </li>

                        <li>
                          <button
                            type="button"
                            className="dropdown-item bl-menu__item"
                            onClick={signOut}
                          >
                            <span className="bl-menu__icon bl-menu__icon--muted">
                              <i className="fa-solid fa-arrow-right-from-bracket" />
                            </span>
                            <div>
                              <div className="bl-menu__title">Sign out</div>
                              <div className="bl-menu__sub">
                                Sign out of your account
                              </div>
                            </div>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Scrim behind the panel */}
        {showSearch && showMobileSearch && (
          <div
            className="bl-msearch__scrim"
            onClick={() => setShowMobileSearch(false)}
          />
        )}

        {/* Mobile search panel */}
        {showSearch && (
          <div
            ref={mobilePanelRef}
            id="mobileSearchPanel"
            className={`bl-msearch d-md-none${
              showMobileSearch ? " is-open" : ""
            }`}
            aria-hidden={!showMobileSearch}
          >
            <form
              className="bl-msearch__form"
              onSubmit={(e) => {
                handleSearch(e);
                setShowMobileSearch(false);
                setShowSuggestions(false);
                setActiveIndex(-1);
              }}
            >
              {/* One field, with the magnifier inside it as the submit. The row
                  used to be a bare close X, a Bootstrap .form-control and a
                  black "Go" button: three separate controls competing for a
                  phone's width, none of them on the theme. */}
              <div className="bl-msearch__field">
                <button
                  type="submit"
                  className="bl-msearch__go"
                  aria-label="Search"
                >
                  <i className="fas fa-search" aria-hidden="true" />
                </button>

                <input
                  ref={mobileSearchRef}
                  type="search"
                  className="bl-msearch__input"
                  placeholder="Search bleeps"
                  value={
                    activeIndex >= 0 && allSuggestionItems[activeIndex]
                      ? allSuggestionItems[activeIndex].text
                      : searchQuery
                  }
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setActiveIndex(-1);
                    setShowSuggestions(e.target.value.trim().length >= 2);
                  }}
                  onKeyDown={handleKeyDown}
                  inputMode="search"
                  /* the on-screen keyboard shows a Search key rather than a
                     newline, which is the other way to submit this */
                  enterKeyHint="search"
                  autoComplete="off"
                />

                {searchQuery && (
                  <button
                    type="button"
                    className="bl-msearch__clear"
                    aria-label="Clear search"
                    onClick={() => {
                      setSearchQuery("");
                      setSuggestions([]);
                      setShowSuggestions(false);
                      setActiveIndex(-1);
                      mobileSearchRef.current?.focus();
                    }}
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                )}
              </div>

              <button
                type="button"
                className="bl-msearch__cancel"
                onClick={() => setShowMobileSearch(false)}
              >
                Cancel
              </button>
            </form>

            {showSuggestions &&
              allSuggestionItems.length > 0 &&
              (() => {
                const dismiss = () => {
                  setShowSuggestions(false);
                  setShowMobileSearch(false);
                  setActiveIndex(-1);
                };
                return (
                  <div className="bl-suggest bl-suggest--inline">
                    {catSuggestions.length > 0 && (
                      <>
                        <div className="bl-menu__label">Categories</div>
                        {catSuggestions.map((cat) => (
                          <Link
                            key={cat}
                            to={`/search?category=${encodeURIComponent(cat)}`}
                            className="bl-suggest__item"
                            onClick={dismiss}
                          >
                            <i className="fa-solid fa-tag bl-suggest__icon bl-suggest__icon--brand" />
                            <span>{cat}</span>
                          </Link>
                        ))}
                      </>
                    )}
                    {catSuggestions.length > 0 && suggestions.length > 0 && (
                      <hr className="bl-menu__divider" />
                    )}
                    {suggestions.length > 0 && (
                      <>
                        {suggestions.map((keyword) => (
                          <Link
                            key={keyword}
                            to={`/search?q=${encodeURIComponent(keyword)}`}
                            className="bl-suggest__item"
                            onClick={dismiss}
                          >
                            <i className="fas fa-search bl-suggest__icon" />
                            <span>{keyword}</span>
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                );
              })()}
          </div>
        )}
      </header>
    </>
  );
};

export default Navbar;
